/**
 * A colour, behind one button.
 *
 * The style bar used to lay every swatch out in a row — eight fills, six
 * line colours, six text colours — which is twenty buttons before any of
 * the controls people came for. On a canvas beside a shape palette that
 * pushed font size off the right-hand edge, where it could not be
 * reached at all and could not be seen to exist.
 *
 * So each colour is a single button showing what is currently set, and
 * the swatches live in a popover under it. The row is a third the width
 * and nothing has to be scrolled to.
 */
import { useEffect, useRef, useState } from 'react';

export default function ColorPick({ label, options, value, onPick }) {
  const [open, setOpen] = useState(false);
  // Where to put the popover, measured from the button when it opens.
  const [at, setAt] = useState(null);
  const host = useRef(null);

  // Closed by anything that means "I am done here": a click elsewhere, or
  // Escape. Without the outside click it stays open behind the next
  // thing somebody does and covers the canvas they are trying to see.
  useEffect(() => {
    if (!open) return undefined;
    const away = (event) => { if (!host.current?.contains(event.target)) setOpen(false); };
    const key = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  const current = options.find((o) => o.value === value);

  /**
   * The popover is FIXED, and positioned from the button.
   *
   * The style bar scrolls sideways rather than wrapping, and anything
   * absolutely positioned inside a scrolling box is clipped by it — so
   * the swatches would open into a strip that had to be scrolled to. A
   * fixed popover escapes the bar entirely. It is nudged left when it
   * would run off the right-hand edge, which on the last control in a
   * bar of a dozen is most of the time.
   */
  const show = () => {
    const box = host.current?.getBoundingClientRect();
    if (box) {
      const width = 168;
      setAt({
        top: Math.round(box.bottom + 4),
        left: Math.round(Math.min(box.left, window.innerWidth - width - 8)),
      });
    }
    setOpen((v) => !v);
  };

  return (
    <span className="cp" ref={host}>
      <button
        type="button"
        className="cp-button"
        aria-label={`${label}${current ? ` — ${current.title}` : ''}`}
        aria-expanded={open}
        title={current?.title || label}
        onClick={show}
      >
        <span
          className={`cp-chip${value === 'transparent' ? ' is-none' : ''}`}
          style={value === 'transparent' ? undefined : { background: value }}
        />
        <span className="cp-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="cp-menu" role="group" aria-label={label} style={at || undefined}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`cp-swatch${value === option.value ? ' is-on' : ''}${option.value === 'transparent' ? ' is-none' : ''}`}
              style={option.value === 'transparent' ? undefined : { background: option.value }}
              title={option.title}
              aria-label={option.title}
              onClick={() => { onPick(option.value); setOpen(false); }}
            />
          ))}
          {/* The eleventh colour. The row above is the ten somebody uses;
              this is everything else, and it stays out of the way until
              it is wanted. */}
          <label className="cp-any">
            <input
              type="color"
              aria-label={`${label} — any colour`}
              value={value && value !== 'transparent' ? value : '#ffffff'}
              onChange={(e) => onPick(e.target.value)}
            />
            <span>Any…</span>
          </label>
        </div>
      )}
    </span>
  );
}
